import { PlusInSquareIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { useBoolean } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Modal, Switch } from "ui";

import { useAddEmailRecipient, useRemoveEmailRecipient } from "../../apis";
import {
  AddRecipientModal,
  EventTypeFilter,
  NoticeBanner,
  RecipientsTable
} from "../../components";
import { EVENT_TYPE_LABELS } from "../../constants";
import { EmailNotificationRecipient } from "../../emailNotifications.types";
import { useEmailRecipientsList } from "../../hooks";

import styles from "./EmailNotifications.module.scss";

const EmailNotifications = () => {
  const { t } = useTranslation();

  const {
    recipients,
    isLoading,
    eventType,
    setEventType,
    includeInactive,
    setIncludeInactive
  } = useEmailRecipientsList();

  const {
    value: isAddModalOpen,
    setTrue: openAddModal,
    setFalse: closeAddModal
  } = useBoolean(false);

  const { user } = useProfile({ enabled: false });
  const { canManageRecipients } = useMemo(() => {
    return {
      canManageRecipients:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.ManageEmailNotificationRecipients
        )
    };
  }, [user]);

  const [recipientToRemove, setRecipientToRemove] =
    useState<EmailNotificationRecipient | null>(null);

  const { mutate: removeRecipient, isLoading: isRemoving } =
    useRemoveEmailRecipient({
      onSuccess: () => {
        toast.success(t("Recipient deactivated"));
        setRecipientToRemove(null);
      },
      onError: toastServerError
    });

  const { mutate: reactivateRecipient } = useAddEmailRecipient({
    onSuccess: () => toast.success(t("Recipient reactivated")),
    onError: toastServerError
  });

  const handleReactivate = (recipient: EmailNotificationRecipient) =>
    reactivateRecipient({
      email: recipient.email,
      adminId: recipient.adminId,
      eventType: recipient.eventType
    });

  return (
    <ContentLayout
      title={t("Email Notifications")}
      action={
        canManageRecipients ? (
          <Button onClick={openAddModal} icon={<PlusInSquareIcon />}>
            {t("Add recipient")}
          </Button>
        ) : null
      }
    >
      <BoxShadowLayout className={styles.page}>
        <NoticeBanner>
          {t(
            "Super Admins are subscribed automatically to all event types. To opt out, deactivate the corresponding row below."
          )}
        </NoticeBanner>

        <div className={styles.page__controls}>
          <EventTypeFilter value={eventType} onChange={setEventType} />

          <Switch
            label={t("Show deactivated")}
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
        </div>

        <RecipientsTable
          recipients={recipients}
          isLoading={isLoading}
          onRemove={setRecipientToRemove}
          onReactivate={handleReactivate}
        />
      </BoxShadowLayout>

      <AddRecipientModal isOpen={isAddModalOpen} onClose={closeAddModal} />

      <Modal
        type="delete"
        isOpen={Boolean(recipientToRemove)}
        isLoading={isRemoving}
        title={t("Deactivate recipient")}
        okText={t("Deactivate")}
        onClose={() => setRecipientToRemove(null)}
        onOk={() =>
          recipientToRemove && removeRecipient(recipientToRemove.id)
        }
      >
        {recipientToRemove
          ? `${t("Stop sending")} "${t(
              EVENT_TYPE_LABELS[recipientToRemove.eventType]
            )}" ${t("emails to")} ${recipientToRemove.email}?`
          : ""}
      </Modal>
    </ContentLayout>
  );
};

export default EmailNotifications;
